import { FC, useEffect, useRef } from 'react';
import { Col, Row } from 'antd';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorView } from '@/shared';

import SettingsTableOfContents from './components/SettingsTableOfContents';
import {
    About,
    AccountSecurity,
    Advanced,
    General,
    Notifications,
    Permissions,
    Plans,
    Preferences,
    Referrals,
    Sessions,
    Workflows,
    WorkspaceGeneral,
    WorkspaceSecurity,
} from './routes';
import Developers from './routes/Workspace/99-Developers';

interface SettingsSearchLocationState {
    settingsSearchRequestId?: number;
    settingsSearchTarget?: string;
    settingsSearchTargetType?: string;
}

const normalizeElementText = (value: string | null | undefined) =>
    value?.replace(/\s+/g, ' ').trim().toLowerCase() ?? '';

const scrollToAndHighlightSettingsTarget = (target: HTMLElement, delay = 0): number =>
    window.setTimeout(() => {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.classList.remove('settings-search-target-highlight');

        window.requestAnimationFrame(() => {
            target.classList.add('settings-search-target-highlight');

            window.setTimeout(() => {
                target.classList.remove('settings-search-target-highlight');
            }, 1800);
        });
    }, delay);

const SettingsSearchTargetHighlighter: FC = () => {
    const location = useLocation();
    const state = (location.state ?? {}) as SettingsSearchLocationState;

    useEffect(() => {
        if (!state.settingsSearchTarget) {
            return;
        }

        const timeout = window.setTimeout(() => {
            const content = document.querySelector('[data-settings-page-content="true"]');

            if (!content) {
                return;
            }

            const normalizedTarget = normalizeElementText(state.settingsSearchTarget);
            const candidates = Array.from(
                content.querySelectorAll<HTMLElement>(
                    [
                        'h1',
                        'h2',
                        'h3',
                        'h4',
                        'h5',
                        'button',
                        'label',
                        'small',
                        '.ant-form-item-label',
                        '.ant-statistic-title',
                        '.ant-tabs-tab-btn',
                        '.ant-typography',
                    ].join(','),
                ),
            );

            const target =
                candidates.find((element) => normalizeElementText(element.textContent) === normalizedTarget) ??
                candidates.find((element) => normalizeElementText(element.textContent).includes(normalizedTarget));

            if (!target) {
                return;
            }

            scrollToAndHighlightSettingsTarget(target);
        }, 150);

        return () => window.clearTimeout(timeout);
    }, [location.key, state.settingsSearchRequestId, state.settingsSearchTarget]);

    return null;
};

const Content: FC = (): JSX.Element => {
    const settingsContentRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <style>
                {`
                    .settings-content-layout {
                        align-items: stretch;
                        box-sizing: border-box;
                        display: grid !important;
                        grid-template-columns: 1rem minmax(0, 1fr) 1rem;
                        height: 100%;
                        min-height: 0;
                        width: 100%;
                    }

                    .settings-content-layout__main {
                        grid-column: 2;
                        justify-self: center;
                        max-width: 62rem;
                        height: 100%;
                        min-height: 0;
                        min-width: 0;
                        width: 100%;
                    }

                    .settings-content-layout__navigator {
                        align-self: stretch;
                        grid-column: 3;
                        min-width: 0;
                        padding-inline-start: 1.5rem;
                    }

                    @media (min-width: 576px) {
                        .settings-content-layout {
                            grid-template-columns: minmax(0, 1fr) minmax(0, 75%) minmax(0, 1fr);
                        }
                    }

                    @media (min-width: 768px) {
                        .settings-content-layout {
                            grid-template-columns: minmax(0, 1fr) minmax(0, 66.6667%) minmax(0, 1fr);
                        }
                    }

                    @media (min-width: 992px) {
                        .settings-content-layout {
                            grid-template-columns: minmax(0, 1fr) minmax(0, 50%) minmax(0, 1fr);
                        }
                    }

                    @media (max-width: 991px) {
                        .settings-content-layout__navigator {
                            display: none;
                        }
                    }
                `}
            </style>
            <Row className='settings-content-layout' data-settings-content='true' wrap={false}>
                <SettingsSearchTargetHighlighter />
                <Col
                    className='settings-content-layout__main'
                    data-settings-page-content='true'
                    ref={settingsContentRef}
                >
                    <Routes>
                        <Route index element={<Navigate to={`/settings/account/general`} replace />} />
                        <Route path='account' element={<Navigate to={`/settings/account/general`} replace />} />
                        <Route path='workspace' element={<Navigate to={`/settings/workspace/general`} replace />} />

                        {/* Account */}
                        <Route path='account/general/*' element={<General />} />
                        <Route path='account/preferences/*' element={<Preferences />} />
                        <Route path='account/notifications/*' element={<Notifications />} />
                        <Route path='account/sessions' element={<Sessions />} />
                        <Route path='account/security' element={<AccountSecurity />} />
                        <Route path='account/plans/*' element={<Plans />} />
                        <Route path='account/referrals' element={<Referrals />} />

                        {/* Workspace */}
                        <Route path='workspace/general/*' element={<WorkspaceGeneral />} />
                        <Route path='workspace/advanced/*' element={<Advanced />} />
                        <Route path='workspace/permissions/*' element={<Permissions />} />
                        <Route path='workspace/workflows' element={<Workflows />} />
                        <Route path='workspace/developers/*' element={<Developers />} />
                        {/* <Route
                            path={`${url}/workspace/integrations/:integration`}
                            component={WorkspaceIntegrationDetail}
                        /> */}
                        <Route path='workspace/security/*' element={<WorkspaceSecurity />} />
                        <Route path='workspace/about' element={<About />} />
                        <Route path='*' element={<ErrorView />} />
                    </Routes>
                </Col>
                <Col className='settings-content-layout__navigator'>
                    <SettingsTableOfContents contentRef={settingsContentRef} />
                </Col>
            </Row>
        </>
    );
};

export default Content;
